/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { useMemo } from 'react';

import {
    Button,
    Card,
    Skeleton,
    Table,
    Flex,
    Typography,
    type SortOrder,
} from '@app/ui';
import { useTranslation } from 'react-i18next';
import { BroadcastViewership } from '@app/types/api';
import * as format from '@app/utils/formatters';
import {
    BROADCAST_IMPRESSIONS,
    INTERNATIONAL_BROADCAST_VIEW,
} from '@app/constants/config';
import { onExportToCsv, getColumns } from '../../utils/viewershipUtils';
import '@app/components/table/table.scss';

import Empty from '@app/components/common/Empty';
import ResizableTable from '@app/components/common/ResizableTable';

interface BroadcastViewershipTableProps {
    broadcasts: BroadcastViewership[];
    client: any;
    selectedBroadcastName?: string;
    loading: boolean;
    viewershipType: string;
}

const BroadcastViewershipTable = ({
    broadcasts = [],
    client,
    selectedBroadcastName,
    loading,
    viewershipType,
}: BroadcastViewershipTableProps) => {
    const { t } = useTranslation();
    const [tableKey, setTableKey] = React.useState(0);

    const hasBroadcastImpressionsPermission = client.permissions.includes(
        BROADCAST_IMPRESSIONS,
    );
    const hasInternationalBroadcastPermission = client.permissions.includes(
        INTERNATIONAL_BROADCAST_VIEW,
    );

    React.useEffect(() => {
        setTableKey(columns => columns + 1); // force re-render when columns change
    }, [viewershipType, loading]);

    const data = useMemo(() => {
        return broadcasts
            .filter(b =>
                selectedBroadcastName ? b.name === selectedBroadcastName : true,
            )
            .flatMap(b =>
                (Array.isArray(b.networkViewerships)
                    ? b.networkViewerships
                    : []
                ).map(n => ({
                    ...n,
                    broadcastDate: b.contentDate,
                    weekDay: format.asWeekDay(b.contentDate),
                    country: n.countryName || 'N/A',
                })),
            );
    }, [broadcasts, selectedBroadcastName]);

    const tableProps = useMemo(() => {
        const columnsRaw = getColumns(
            {},
            client.currency?.code,
            viewershipType,
            hasInternationalBroadcastPermission,
            t,
        );
        const columns = columnsRaw.map(c => {
            const {
                Header,
                accessor,
                format: formatter,
                width,
            } = c as {
                Header: string;
                accessor: string;
                format?: (value: any) => any;
                id?: string;
                width?: number;
            };

            return {
                title: Header,
                dataIndex: accessor,
                key: accessor,
                ellipsis: true,
                width: width ?? 150,
                sorter: (a, b) => {
                    const aValue = a[accessor];
                    const bValue = b[accessor];

                    if (aValue === undefined || bValue === undefined) {
                        return 0;
                    }
                    if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue);
                    }
                    return aValue - bValue;
                },
                sortDirections: ['descend', 'ascend'] as SortOrder[],
                render: (value: any) => {
                    if (typeof formatter === 'function') {
                        return formatter(value);
                    }
                    return value;
                },
            };
        });

        const summary = () => (
            <Table.Summary.Row
                style={{
                    borderRadius: '0 0 12px 12px',
                    overflow: 'hidden',
                }}
            >
                {columns.map((col, index) => {
                    const { dataIndex, render } = col;

                    if (index === 0) {
                        return (
                            <Table.Summary.Cell
                                key={col.key}
                                index={index}
                                className="summary-cell first-cell"
                            >
                                {t('measurement:broadcastExposure.table.total')}
                            </Table.Summary.Cell>
                        );
                    }

                    // Skip summing known non-numeric fields
                    if (
                        ['broadcastDate', 'weekDay', 'country'].includes(
                            dataIndex,
                        )
                    ) {
                        return (
                            <Table.Summary.Cell
                                key={col.key}
                                index={index}
                                className="summary-cell"
                            />
                        );
                    }

                    const values = data.map(row => row[dataIndex]);
                    const numericValues = values.filter(
                        v => typeof v === 'number',
                    );
                    const total = numericValues.reduce(
                        (sum, val) => sum + val,
                        0,
                    );

                    return (
                        <Table.Summary.Cell
                            key={col.key}
                            index={index}
                            className={`summary-cell ${index === columns.length - 1 ? 'last-cell' : ''}`}
                        >
                            {typeof render === 'function'
                                ? render(total)
                                : total}
                        </Table.Summary.Cell>
                    );
                })}
            </Table.Summary.Row>
        );
        return {
            rowKey: (record: any, index: number) =>
                `${record.country}-${record.weekDay}-${index}`,
            columns,
            dataSource: data,
            loading,
            pagination: {
                pageSize: 9,
                showSizeChanger: false,
            },
            bordered: true,
            scroll: { y: '60vh' },
            locale: {
                emptyText: loading ? (
                    <Skeleton active paragraph={{ rows: 10 }} />
                ) : (
                    <div
                        style={{
                            minHeight: 200,
                        }}
                    />
                ),
            },
            summary,
        };
    }, [
        data,
        loading,
        viewershipType,
        hasInternationalBroadcastPermission,
        client,
        selectedBroadcastName,
        t,
    ]);

    return (
        <Card
            style={{
                margin: '24px 8px 0 8px',
            }}
        >
            <Flex
                align="center"
                style={{
                    marginBottom: 24,
                }}
            >
                <Flex flex={1}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                        }}
                    >
                        {t('measurement:broadcastExposure.table.title')}
                    </Typography.Title>
                </Flex>

                <Flex>
                    <Button
                        onClick={() =>
                            onExportToCsv(
                                data,
                                client.csvDelimiter || ',',
                                hasBroadcastImpressionsPermission,
                                hasInternationalBroadcastPermission,
                                viewershipType,
                                client.currency?.code,
                                t,
                            )
                        }
                        className="-m-l-2"
                        data-test="insights-download"
                        type="primary"
                        color="primary"
                        variant="solid"
                    >
                        <i className="fa-regular fa-download -m-r-1" />
                        {t('measurement:broadcastExposure.table.downloadCsv')}
                    </Button>
                </Flex>
            </Flex>
            {tableKey > 0 && (
                <div
                    className="broadcast-viewership-table"
                    style={{ width: '100%', minWidth: 0, position: 'relative' }}
                >
                    <ResizableTable key={tableKey} {...tableProps} />
                    {!loading && data.length === 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate3d(-50%, -50%, 0)',
                            }}
                        >
                            <Empty />
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default BroadcastViewershipTable;
